import { Icon, type IconName } from '@/design-system/ui/Icon';
import { cn } from '@/design-system/ui/cn';
import type { HistoryStep } from '../model/schemas';

const ICONS: IconName[] = ['doc', 'user', 'box', 'home'];
const STEP_MS = 140;

/**
 * Horizontal progress for the top of a shipment card (light-on-dark): registered → driver → picked up → delivered.
 * Lines between finished steps fill in one after another and the current step pulses.
 * The full vertical list further down the page stays as the detailed, screen-reader friendly version.
 */
export function TrackProgress({ history }: { history: HistoryStep[] }) {
  const currentIndex = history.reduce((acc, s, i) => (s.done ? i : acc), -1);
  return (
    <ol className="flex items-start" aria-label="پیشرفت مرسوله">
      {history.map((step, i) => {
        const last = i === history.length - 1;
        const cancelled = step.label === 'سفارش لغو شد';
        const current = i === currentIndex && !cancelled && !last;
        const filled = step.done && history[i + 1]?.done;
        return (
          <li key={step.label} className="relative flex min-w-0 flex-1 flex-col items-center gap-2 text-center" aria-current={i === currentIndex ? 'step' : undefined}>
            {!last && (
              <span className="absolute start-1/2 top-[1.125rem] h-0.5 w-full overflow-hidden rounded-full bg-white/20" aria-hidden>
                {filled && <span className="absolute inset-0 origin-right animate-grow-x rounded-full bg-white" style={{ animationDelay: `${i * STEP_MS + 250}ms` }} />}
              </span>
            )}
            <span className="relative z-10 grid size-9 place-items-center">
              {current && <span className="absolute inset-0 animate-ping rounded-full bg-white/40 [animation-duration:2s]" aria-hidden />}
              <span
                className={cn(
                  'relative grid size-9 animate-pop place-items-center rounded-full ring-2',
                  cancelled ? 'bg-danger text-white ring-white/60' : step.done ? 'bg-white text-action ring-white/70 shadow-[0_6px_16px_rgb(4_15_27/0.35)]' : 'bg-white/10 text-white/60 ring-white/25',
                )}
                style={{ animationDelay: `${i * STEP_MS}ms`, animationFillMode: 'both' }}
              >
                <Icon name={cancelled ? 'close' : (ICONS[i] ?? 'box')} size={17} />
              </span>
            </span>
            <span className={cn('px-0.5 text-[11px] leading-snug sm:text-xs', step.done ? 'font-bold text-white' : 'text-white/60')}>
              {step.label}
              <span className="sr-only">{step.done ? ' — انجام شد' : ' — در انتظار'}</span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}
